<template>
  <div :class="outerContainerClasses">
    <!-- Main form field container -->
    <div :class="containerClasses">
      <label :for="name" :class="labelClasses" :style="labelStyles">
        {{ label }}
        <span v-if="required && showRequiredIndicator" class="text-red-400 ml-1">*</span>
      </label>

      <div :class="textareaWrapperClasses">
        <textarea
          :id="name"
          :name="name"
          :placeholder="placeholder"
          :class="[textareaClasses, { 'red-border': hasError }]"
          :value="inputValue"
          @input="handleInput"
          @blur="handleBlur"
          :rows="rows"
          v-bind:style="computedTextareaStyles"
          :disabled="disabled"
          :readonly="readonly"
          :maxlength="maxlength"
        ></textarea>

        <!-- Absolute positioned error message -->
        <div
          v-if="hasError && showErrors && errorPosition === 'absolute'"
          :class="errorMessageAbsoluteClasses"
          :style="errorMessageStyles"
        >
          {{ errorMessage }}
        </div>

        <!-- Below textarea positioned error message -->
        <div
          v-if="hasError && showErrors && errorPosition === 'below'"
          :class="errorMessageBelowClasses"
          :style="errorMessageStyles"
        >
          {{ errorMessage }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useField } from 'vee-validate';
import { computed, toRef, watch } from 'vue';
import { defaultFormStyles, mergeStyles } from '@/validations/styles/themeConfig.js';

export default {
  name: 'ValidatedTextarea',
  props: {
    name: {
      type: String,
      required: true
    },
    label: {
      type: String,
      required: true
    },
    placeholder: {
      type: String,
      default: ''
    },
    rows: {
      type: Number,
      default: 3
    },
    maxlength: {
      type: Number,
      default: null
    },
    required: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    readonly: {
      type: Boolean,
      default: false
    },
    showErrors: {
      type: Boolean,
      default: false // Disabled by default to match existing behavior
    },
    showRequiredIndicator: {
      type: Boolean,
      default: false // Disabled by default to match existing behavior
    },
    errorPosition: {
      type: String,
      default: 'below', // 'below', 'absolute', 'none'
      validator: (value) => ['below', 'absolute', 'none'].includes(value)
    },
    showCharCount: {
      type: Boolean,
      default: false
    },
    // For backward compatibility with existing components
    modelValue: {
      type: String,
      default: ''
    },
    // Validation rules (optional, can be used without form context)
    rules: {
      type: [String, Object, Function],
      default: undefined
    },
    // Style customization
    customStyles: {
      type: Object,
      default: () => ({})
    },
    // Alternative to use existing styling exactly
    useExistingStyle: {
      type: Boolean,
      default: true
    }
  },
  emits: ['update:modelValue'],
  setup(props, { emit }) {
    const name = toRef(props, 'name');

    // Use VeeValidate's useField composable
    const {
      value: inputValue,
      errorMessage,
      handleBlur,
      handleChange,
      meta
    } = useField(name, props.rules, {
      initialValue: props.modelValue,
      validateOnValueUpdate: true,
      syncVModel: true
    });

    // Computed property for error state
    const hasError = computed(() => {
      // Show errors only after the field has been touched/interacted with
      // This prevents showing errors immediately on page load
      return !!(errorMessage.value && (meta.touched || meta.dirty));
    });

    // Style configuration
    const styleConfig = computed(() => {
      if (props.useExistingStyle) {
        // Use exact same styles as existing forms - flex layout with specific widths
        return {
          container: {
            classes: 'flex justify-between items-start'
          },
          label: {
            classes: 'text-blue-100 text-right w-1/6 pt-2',
            styles: 'color: var(--label);'
          },
          textarea: {
            classes: 'rounded text-xs p-2 placeholder-gray-500 w-full',
            styles: {
              normal: {},
              error: {
                backgroundColor: 'var(--obrigatorio)',
                color: 'var(--text)'
              }
            }
          },
          errorMessage: {
            classes: 'text-xs mt-1',
            styles: 'color: var(--vermelho-2, #FD5D5D);'
          },
          errorMessageAbsolute: {
            classes: 'absolute top-full left-0 text-xs mt-1 z-10 bg-white px-2 py-1 rounded shadow-md border border-red-300',
            styles: 'color: var(--vermelho-2, #FD5D5D);'
          },
          errorMessageBelow: {
            classes: 'text-xs mt-1 w-full',
            styles: 'color: var(--vermelho-2, #FD5D5D);'
          }
        };
      }
      return mergeStyles(props.customStyles);
    });

    // Computed styles and classes
    const outerContainerClasses = computed(() => {
      return 'relative'; // For absolute positioning of errors
    });

    const containerClasses = computed(() => {
      return styleConfig.value.container.classes;
    });

    const textareaWrapperClasses = computed(() => {
      // Check if using custom styles and custom textareaWrapper is provided
      if (!props.useExistingStyle && props.customStyles?.textareaWrapper?.classes) {
        const baseClasses = props.errorPosition === 'absolute' ? 'relative' : '';
        return `${baseClasses} ${props.customStyles.textareaWrapper.classes}`.trim();
      }
      // Default behavior for existing style
      return props.errorPosition === 'absolute' ? 'relative w-3/4' : 'w-3/4';
    });

    const labelClasses = computed(() => {
      return styleConfig.value.label.classes;
    });

    const labelStyles = computed(() => {
      return styleConfig.value.label.styles;
    });

    const textareaClasses = computed(() => {
      return styleConfig.value.textarea.classes;
    });

    const computedTextareaStyles = computed(() => {
      let state = 'normal';
      if (props.disabled) {
        state = 'disabled';
      } else if (hasError.value) {
        state = 'error';
      }
      return styleConfig.value.textarea.styles[state] || styleConfig.value.textarea.styles.normal;
    });

    const errorMessageClasses = computed(() => {
      return styleConfig.value.errorMessage.classes;
    });

    const errorMessageAbsoluteClasses = computed(() => {
      return styleConfig.value.errorMessageAbsolute?.classes || 'absolute top-full left-0 text-xs mt-1 z-10 bg-white px-2 py-1 rounded shadow-md border border-red-300';
    });

    const errorMessageBelowClasses = computed(() => {
      return styleConfig.value.errorMessageBelow?.classes || 'text-xs mt-1 w-full';
    });

    const errorMessageStyles = computed(() => {
      return styleConfig.value.errorMessage.styles;
    });

    // Computed property for character count
    const characterCount = computed(() => {
      return (inputValue.value || '').length;
    });

    // Handle input changes
    const handleInput = (event) => {
      const value = event.target.value;

      // Update VeeValidate field value
      handleChange(value);

      // Emit for v-model compatibility
      emit('update:modelValue', value);
    };

    // Watch for external modelValue changes (for backward compatibility)
    watch(() => props.modelValue, (newValue) => {
      if (newValue !== inputValue.value) {
        inputValue.value = newValue;
      }
    });

    return {
      inputValue,
      errorMessage,
      handleInput,
      handleBlur,
      handleChange,
      meta,
      hasError,
      styleConfig,
      outerContainerClasses,
      containerClasses,
      textareaWrapperClasses,
      labelClasses,
      labelStyles,
      textareaClasses,
      computedTextareaStyles,
      errorMessageClasses,
      errorMessageAbsoluteClasses,
      errorMessageBelowClasses,
      errorMessageStyles,
      characterCount
    };
  }
};
</script>

<style scoped>
.red-border {
  background-color: var(--obrigatorio);
}

.red-border:focus {
  border: 1px solid var(--vermelho-2);
}

/* Remove custom borders and focus styles to match existing behavior */
textarea:focus {
  outline: none;
}

</style>